<script setup lang="ts">
import { computed } from 'vue'
import { router } from '@inertiajs/vue3'
import { Check, Radio, UserRound, CheckCircle } from 'lucide-vue-next'
import { BINDLE_LIVE_LAUNCH_PRICE, offersOneToOne } from '@/composables/useTutorPlans'

interface Subscription {
  teacher_id: number
  tier: string
  expires_in: number | null
}

interface Props {
  tutorSlug: string
  tutorId?: number
  tutorName?: string
  basePrice: number
  silverPrice: number
  goldPrice: number
  videosCount: number
  servicesOffered: string[]
  groupSubjects?: Array<{ id: number; name: string; levels: Array<{ id: number; name: string }> }>
  subscriptions?: Subscription[]
}

const props = withDefaults(defineProps<Props>(), {
  groupSubjects: () => [],
  subscriptions: () => [],
})

/**
 * The sidebar summary of the same two plans the Plans tab shows: Bindle Live,
 * then 1:1.
 *
 * The content-library plan is gone from the front end — it is included in both
 * plans rather than sold beside them — and group tuition is what Bindle Live
 * replaces, so the silver tier is that card's slot. Neither tier is deleted; see
 * PricingPlans.vue for the full reasoning and `useTutorPlans` for the facts this
 * card and that one now share.
 */
const activeSub = computed(() => props.subscriptions.find((s) => s.teacher_id === props.tutorId))
const isSubscribed = computed(() => !!activeSub.value)
const subscribedTier = computed(() => activeSub.value?.tier ?? null)

const oneToOneAvailable = computed(() =>
  offersOneToOne({ goldPrice: props.goldPrice, servicesOffered: props.servicesOffered })
)

function subscribe(tier: string) {
  if (isSubscribed.value) return
  router.visit(`/payment?tutor=${props.tutorSlug}&tier=${tier}`)
}

const scrollToPlans = () => {
  document.querySelector('[data-plans]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<template>
  <!--
    Not sticky. Pinned at `top-24` this card sat over the profile the visitor was
    scrolling through, and on a laptop viewport its own subscribe button fell off
    the bottom of the pinned area where nothing could reach it.
  -->
  <section class="surface-card p-6">
    <div class="mb-5">
      <h3 class="font-sans text-base font-bold text-ink">Choose a plan</h3>
      <p class="mt-1 text-sm text-muted">Billed weekly. Cancel any time.</p>
    </div>

    <!-- Bindle Live -->
    <div class="rounded-xl border border-dashed border-line-strong p-4">
      <div class="flex items-start justify-between gap-3">
        <span class="grid size-9 place-items-center rounded-full bg-live-soft text-live-soft-text">
          <Radio class="size-4.5" />
        </span>
        <span class="rounded-full bg-live-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-live-soft-text">
          Soon
        </span>
      </div>
      <p class="mt-3 font-sans text-sm font-bold uppercase tracking-[0.08em] text-live-soft-text">Bindle Live</p>
      <p class="mt-1 text-xs text-muted">Live weekly group classes, recorded into your library.</p>
      <p class="mt-2 text-xs text-subtle">
        Planned
        <strong class="font-display text-lg text-live-soft-text">£{{ BINDLE_LIVE_LAUNCH_PRICE.toFixed(2) }}</strong>/week
      </p>
    </div>

    <!-- 1:1 -->
    <div
      class="mt-3 rounded-xl border-2 p-4 transition-colors"
      :class="subscribedTier === 'gold'
        ? 'border-success bg-success-bg'
        : oneToOneAvailable
          ? 'cursor-pointer border-brand hover:bg-surface-brand'
          : 'border-line bg-surface-2 opacity-70'"
      @click="oneToOneAvailable && !isSubscribed && subscribe('gold')"
    >
      <div class="flex items-start justify-between gap-3">
        <span class="grid size-9 place-items-center rounded-full bg-brand-soft text-brand">
          <UserRound class="size-4.5" />
        </span>
        <span
          v-if="subscribedTier === 'gold'"
          class="inline-flex items-center gap-1 rounded-full bg-success px-2 py-0.5 text-[10px] font-bold text-white"
        >
          <CheckCircle class="size-3" /> Subscribed
        </span>
      </div>
      <p class="mt-3 font-sans text-sm font-bold uppercase tracking-[0.08em] text-brand-soft-text">1:1 Tutoring</p>
      <p class="mt-1 text-xs text-muted">A weekly lesson with {{ tutorName || 'this tutor' }}, plus their library.</p>
      <p v-if="oneToOneAvailable" class="mt-2 text-xs text-subtle">
        <strong class="font-display text-lg text-ink">£{{ Number(goldPrice).toFixed(2) }}</strong>/week
      </p>
      <p v-else class="mt-2 text-xs font-semibold text-muted">Not offered by this tutor</p>
    </div>

    <button
      v-if="!isSubscribed && oneToOneAvailable"
      class="mt-5 w-full rounded-lg bg-brand py-3 font-semibold text-brand-contrast transition-colors hover:bg-brand-hover"
      @click="subscribe('gold')"
    >
      Subscribe to 1:1
    </button>
    <button
      v-else
      class="mt-5 w-full rounded-lg border border-line py-3 font-semibold text-ink transition-colors hover:bg-surface-2"
      @click="scrollToPlans"
    >
      Compare the plans
    </button>

    <!--
      "30-day money-back guarantee" was here. There is no such guarantee in the
      terms and no refund mechanism in the product; what is true is that
      subscriptions are weekly and stop when you cancel.
    -->
    <p class="mt-4 flex items-center gap-2 text-xs text-muted">
      <Check class="size-4 shrink-0 text-brand" />
      Billed weekly · cancel any time · no minimum term
    </p>
  </section>
</template>
